/**
 * Cliente HTTP da pré-análise de TRIAGEM (E45.F2) — raia isolada (ADR-0056). // ux-neutrality:allow doc-comment
 *
 * Espelha apps/api/types/pre_analise.py e apps/api/runtime/pre_analise.py. // ux-neutrality:allow doc-comment
 * A triagem é um apoio PRELIMINAR amigável: indicativo em % + tendência // ux-neutrality:allow doc-comment
 * DERIVADA do % (single-source no backend) + marcações + síntese. NUNCA entra // ux-neutrality:allow doc-comment
 * no laudo nem encosta no LR determinístico. // ux-neutrality:allow doc-comment
 *
 * Coordenadas das marcações: geometria NORMALIZADA à imagem ([0,1]), o mesmo // ux-neutrality:allow doc-comment
 * primitivo das anotações da mesa (lib/mesa_annotations) — o overlay reusa a // ux-neutrality:allow doc-comment
 * tática de SVG do heatmap (E26/E36/E38). // ux-neutrality:allow doc-comment
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';
import type { AnnotationGeometryData } from '@/lib/mesa_annotations';
import { nomeDoAnexo, type ArquivoBaixado } from '@/lib/http_download';

// Contrato ----------------------------------------------------------------

export type Tendencia = 'compatibilidade' | 'inconclusivo' | 'divergencia';
export type MarcacaoTipo = 'circulo' | 'seta';
export type MarcacaoRotulo = 'convergencia' | 'divergencia';
export type PreAnaliseStatus =
  | 'em_triagem'
  | 'encerrada'
  /** E72.F1 — encerrada emitindo o Relatório Preliminar (ADR-0074 §2.5). */
  | 'encerrada_com_relatorio'
  | 'seguiu_mesa';
export type PreAnaliseAcao = 'seguir_mesa' | 'finalizar' | 'finalizar_com_relatorio';

/** Os desfechos que encerram o caso NA TRIAGEM — espelha `PRE_ANALISE_STATUS_ENCERRAMENTOS`. */
export const PRE_ANALISE_ENCERRAMENTOS: readonly PreAnaliseStatus[] = [
  'encerrada',
  'encerrada_com_relatorio',
];

/**
 * Leitura preliminar da clareza do material comparado (E47.F2.T01, ADR-0058 §2.3).
 * Espelha `ClarezaIndicios` em apps/api/types/pre_analise.py. NÃO é veredito de
 * "dispensa perito": a UI sempre a exibe com disclaimer não-pericial (a decisão
 * é do perito humano).
 */
export type ClarezaIndicios = 'claros' | 'ambiguos';

/**
 * Banda de inconclusivo no meio da faixa (ADR-0056). Espelha
 * `INCONCLUSIVO_BAND_MIN/MAX` em apps/api/types/pre_analise.py — a tendência já
 * vem derivada do backend; estes limites servem só para DESENHAR a zona na
 * barra. Mantidos em sincronia manual (não há fronteira de import Py↔TS).
 */
export const INCONCLUSIVO_BAND_MIN = 40;
export const INCONCLUSIVO_BAND_MAX = 60;

export interface MarcacaoData {
  tipo: MarcacaoTipo;
  regiao: AnnotationGeometryData;
  rotulo: MarcacaoRotulo;
  observacao: string;
}

export interface PreAnaliseRecordData {
  id: string;
  case_id: string;
  contested_signature_id: string;
  indicativo_percentual: number;
  tendencia: Tendencia;
  clareza_indicios: ClarezaIndicios;
  marcacoes: MarcacaoData[];
  sintese: string;
  raw_output: Record<string, unknown>;
  status: PreAnaliseStatus;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// Erros tipados -----------------------------------------------------------

export type PreAnaliseErrorReason =
  | 'case_not_found'
  | 'contested_not_found'
  | 'no_api_key'
  | 'insufficient_signatures'
  | 'contract'
  | 'assistant_unavailable'
  | 'not_found'
  | 'invalid_transition'
  /** E72.F2 — o caso não foi encerrado COM relatório: não há documento a servir. */
  | 'relatorio_not_ready'
  | 'network'
  | 'unknown';

export class PreAnaliseError extends Error {
  public readonly reason: PreAnaliseErrorReason;

  constructor(reason: PreAnaliseErrorReason) {
    super(`pre_analise_error:${reason}`);
    this.name = 'PreAnaliseError';
    this.reason = reason;
  }
}

const _DETAIL_TO_REASON: Record<string, PreAnaliseErrorReason> = {
  CASE_NOT_FOUND: 'case_not_found',
  CONTESTED_SIGNATURE_NOT_FOUND: 'contested_not_found',
  USER_HAS_NO_API_KEY: 'no_api_key',
  PRE_ANALISE_INSUFFICIENT_SIGNATURES: 'insufficient_signatures',
  PRE_ANALISE_CONTRACT: 'contract',
  PRE_ANALISE_NOT_FOUND: 'not_found',
  PRE_ANALISE_INVALID_TRANSITION: 'invalid_transition',
  PRE_ANALISE_RELATORIO_NOT_READY: 'relatorio_not_ready',
  ASSISTANT_TIMEOUT: 'assistant_unavailable',
  ASSISTANT_UNAVAILABLE: 'assistant_unavailable',
  ASSISTANT_VALIDATION_FAILED: 'assistant_unavailable',
};

/**
 * Reidrata o corpo de erro quando a chamada pediu `responseType: 'blob'`
 * (E72.F2): axios entrega o `{detail}` do 422 como Blob, e a classificação por
 * `detail` cairia sempre em 'unknown'. Mesma tática de `lib/laudo.ts`.
 */
async function rehydrateBlobError(error: unknown): Promise<unknown> {
  if (!axios.isAxiosError(error)) return error;
  const data = error.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed: unknown = JSON.parse(await data.text());
      (error.response as { data: unknown }).data = parsed;
    } catch {
      // Corpo não-JSON (binário truncado, HTML de proxy) — mantém como veio.
    }
  }
  return error;
}

function classifyError(error: unknown): PreAnaliseError {
  if (axios.isAxiosError(error)) {
    const axErr = error as AxiosError<{ detail?: string }>;
    if (axErr.response === undefined) return new PreAnaliseError('network');
    const detail = axErr.response.data?.detail;
    const reason = detail === undefined ? undefined : _DETAIL_TO_REASON[detail];
    if (reason !== undefined) return new PreAnaliseError(reason);
  }
  return new PreAnaliseError('unknown');
}

// Rotas -------------------------------------------------------------------

function preAnalisePath(caseId: string): string {
  return `/casos/${caseId}/pre-analise`;
}

// E47.F2.T06: a triagem real leva ~20-40s (comparação visual + ensemble de 3);
// o timeout default do cliente (15s) abortava TODA execução real como "sem
// conexão" — com o servidor completando e gravando depois (execução duplicada
// quando o perito tentava de novo). Timeout próprio, folgado sobre o pior caso
// do backend (60s por chamada do agente).
const RUN_PRE_ANALISE_TIMEOUT_MS = 120_000;

/**
 * POST: dispara a triagem da contestada × padrões e devolve o resultado.
 *
 * `idempotencyKey` (E50.F4.C02, F-E50.F4-02): com a chave, repetir o POST
 * devolve o registro já criado — a resposta pode se perder DEPOIS de o
 * servidor gravar (janela do timeout acima) e a retentativa não pode duplicar
 * o exame. O portal envia chave estável por contestada; o perito não envia
 * (re-rodar a triagem segue criando registro novo).
 */
export async function runPreAnalise(
  client: AxiosInstance,
  caseId: string,
  contestedSignatureId: string,
  options?: { idempotencyKey?: string },
): Promise<PreAnaliseRecordData> {
  try {
    const { data } = await client.post<PreAnaliseRecordData>(
      preAnalisePath(caseId),
      {
        contested_signature_id: contestedSignatureId,
        ...(options?.idempotencyKey !== undefined
          ? { idempotency_key: options.idempotencyKey }
          : {}),
      },
      { timeout: RUN_PRE_ANALISE_TIMEOUT_MS },
    );
    return data;
  } catch (error) {
    throw classifyError(error);
  }
}

/** GET: lista cronológica das pré-análises do caso (a UI usa a mais recente). */
export async function listPreAnalises(
  client: AxiosInstance,
  caseId: string,
): Promise<PreAnaliseRecordData[]> {
  try {
    const { data } = await client.get<PreAnaliseRecordData[]>(preAnalisePath(caseId));
    return data;
  } catch (error) {
    throw classifyError(error);
  }
}

/**
 * PATCH: aplica a ação a TODAS as triagens pendentes do caso (E67.F1).
 *
 * O portão do perito tem uma triagem por contestada e a decisão é do caso, não
 * da CCB. Uma request, uma transação no servidor: N chamadas à rota unitária
 * deixariam metade das contestadas na mesa e metade na triagem se a rede
 * falhasse no meio.
 */
export async function applyPreAnaliseAcaoDoCaso(
  client: AxiosInstance,
  caseId: string,
  acao: PreAnaliseAcao,
): Promise<PreAnaliseRecordData[]> {
  try {
    const { data } = await client.patch<PreAnaliseRecordData[]>(preAnalisePath(caseId), { acao });
    return data;
  } catch (error) {
    throw classifyError(error);
  }
}

/**
 * PATCH: aplica 'seguir para a mesa' ou 'finalizar na triagem' a UMA triagem.
 *
 * Continua servindo quem age sobre uma questionada específica; o portão do
 * perito passou a usar a ação do caso acima.
 */
export async function applyPreAnaliseAcao(
  client: AxiosInstance,
  caseId: string,
  preAnaliseId: string,
  acao: PreAnaliseAcao,
): Promise<PreAnaliseRecordData> {
  try {
    const { data } = await client.patch<PreAnaliseRecordData>(
      `${preAnalisePath(caseId)}/${preAnaliseId}`,
      { acao },
    );
    return data;
  } catch (error) {
    throw classifyError(error);
  }
}

const RELATORIO_FALLBACK_FILENAME = 'relatorio-preliminar.pdf';

/**
 * GET: o Relatório Preliminar em PDF do caso encerrado COM relatório (E72.F1).
 *
 * O servidor RE-RENDERIZA sob demanda (ADR-0074 §2.6) — não há arquivo guardado,
 * então baixar de novo devolve o mesmo documento. 422
 * `PRE_ANALISE_RELATORIO_NOT_READY` para qualquer outro desfecho: o caso
 * encerrado SEM documento não ganha um pela URL.
 */
export async function baixarRelatorioPreliminar(
  client: AxiosInstance,
  caseId: string,
): Promise<ArquivoBaixado> {
  try {
    const resposta = await client.get<Blob>(`${preAnalisePath(caseId)}/relatorio.pdf`, {
      responseType: 'blob',
    });
    return {
      blob: resposta.data,
      // O nome sai do servidor, que já o compõe com a pasta e o contrato do
      // caso e o saneia (RFC 6266). O recuo é neutro e nunca vaza identificador.
      filename: nomeDoAnexo(resposta.headers['content-disposition'], RELATORIO_FALLBACK_FILENAME),
    };
  } catch (error) {
    throw classifyError(await rehydrateBlobError(error));
  }
}
